import {
  ga4RawParams,
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
  type AnalyticsProperties,
} from "@/lib/analytics-ga4";
import { getOrCreateBootstrapDistinctId } from "@/lib/analytics-identity";

const SERVER_EVENT_NAMES = new Set([
  "agent_revenue_offer_clicked",
  "agent_revenue_offer_dismissed",
  "agent_revenue_offer_shown",
  "agentic_cart_bundle_shown",
  "agentic_cart_followup_sent",
  "agentic_cart_opened",
  "agentic_cart_order_created",
  "agentic_cart_paid",
  "agentic_cart_shopify_mirrored",
  "ai_chat_reply_received",
  "ai_astrologer_opened",
  "astrologer_page_viewed",
  "calculator_used",
  "chat_limit_reached",
  "chat_message_sent",
  "chat_started",
  "chat_tool_invoked",
  "contact_captured",
  "first_meaningful_question",
  "free_tool_completed",
  "free_tool_started",
  "internal_link_clicked",
  "kundali_form_started",
  "kundali_form_submitted",
  "kundali_form_submit_failed",
  "kundali_generated",
  "kundali_result_viewed",
  "matching_completed",
  "outbound_link_clicked",
  "page_engaged",
  "paid_cta_clicked",
  "paid_report_nudge_shown",
  "paywall_shown",
  "premium_unlock_shown",
  "report_checkout_abandoned",
  "report_checkout_form_shown",
  "report_checkout_recovery_clicked",
  "report_checkout_recovered",
  "report_checkout_started",
  "report_offer_cta_tapped",
  "report_delivered",
  "report_purchased",
  "report_remedy_kit_upsell_clicked",
  "report_remedy_kit_upsell_shown",
  "report_upi_payment_timeout",
  "chat_upi_payment_timeout",
  "revenue_completed",
  "razorpay_checkout_closed",
  "razorpay_checkout_opened",
  "scroll_depth",
  "seo_agent_bridge_clicked",
  "seo_agent_bridge_viewed",
  "seo_intent_captured",
  "seo_page_viewed",
  "shopify_cart_started",
  "shopify_outbound_click",
  "shopify_product_clicked",
  // site_page_viewed removed 2026-05-24 — duplicate of $pageview;
  // ~3.4K events/day toward the 1M/mo PostHog free-tier cap.
]);

/**
 * True for events whose canonical GA4 writer is the backend (either the
 * edge beacon for browser-originated events, or the Razorpay verify
 * webhook for paid commerce). Clients call this so `trackEvent` can
 * skip its gtag/GTM fanout for these — otherwise GA4 receives 2-3× per fire.
 */
export function isServerCanonicalEvent(name: string): boolean {
  return SERVER_EVENT_NAMES.has(name);
}

// Server-canonical events whose canonical home is the first-party BigQuery
// sink (events_raw) — NOT GA4. They are funnel/diagnostic mirrors of the
// real purchase that would otherwise land in GA4 as purchase-looking custom
// events and pollute the ecommerce picture (the 2026-06-13 cleanup). GA4
// ecommerce is the reserved `purchase` event only, fired server-side from
// /payment/verify. These still flow to events_raw for funnel analysis; they
// just stop being forwarded to GA4.
const GA4_EXCLUDED_SERVER_EVENT_NAMES = new Set(["report_purchased", "revenue_completed"]);

function isAutomatedAuditBrowser() {
  const ua = navigator.userAgent;
  return /\b(Lighthouse|Chrome-Lighthouse|HeadlessChrome)\b/i.test(ua);
}

interface QueuedEvent {
  name: string;
  client_id: string;
  session_id?: string;
  forward_to_ga4?: boolean;
  page_location: string;
  page_title: string;
  page_referrer: string;
  engagement_time_msec: number;
  params: Record<string, unknown>;
}

// Tracks the timestamp at which the current page became visible. Used to
// derive `engagement_time_msec` per event so GA4 stops marking every
// server-canonical event as "non-engaged". Reset on `pagehide`,
// `visibilitychange` (when tab becomes visible again), and SPA route
// transitions (callers invoke `markRouteChange()` on `pathname` change).
let pageVisibleSince = typeof window === "undefined" ? 0 : Date.now();

export function markRouteChange(): void {
  if (typeof window === "undefined") return;
  pageVisibleSince = Date.now();
}

function currentEngagementMs(): number {
  if (typeof window === "undefined") return 1;
  if (document.visibilityState === "hidden") return 1;
  const elapsed = Date.now() - pageVisibleSince;
  // Cap at 30 min — anything higher is almost certainly an idle tab, not
  // genuine engagement, and GA4 itself caps engagement_time_msec at 30 min
  // per event.
  return Math.max(1, Math.min(elapsed, 1_800_000));
}

// Batching parameters — chosen to reduce beacon request count without sampling.
// pagehide still flushes short sessions before the browser leaves the page, and
// conversion-critical events below bypass the long timer.
const BATCH_FLUSH_MS = 30_000;
const BATCH_MAX_EVENTS = 50;
const IMMEDIATE_FLUSH_EVENT_NAMES = new Set([
  "agentic_cart_order_created",
  "agentic_cart_paid",
  "chat_limit_reached",
  "contact_captured",
  // A/B exposure MUST deliver reliably or the split skews (Sample Ratio Mismatch):
  // an arm with more friction (e.g. the phone-gate "required"/"optional" arms) makes
  // users bounce BEFORE a 30s-batched exposure flushes, so its exposures are
  // under-logged relative to a smoother arm → the split looks imbalanced and the CVR
  // verdict becomes untrustworthy (phone_gate_2026_07 SRM p=0.002, 2026-07-09).
  // Exposure is ≤1/session/experiment (deduped), so immediate flush is cheap.
  "experiment_exposed",
  // A JS error often precedes a crash/navigation, so flush it before the page
  // can unmount. The js_error emitter caps at ≤5 beacons/session (de-duped), so
  // immediate flush is at most 5 POSTs — well within the edge-beacon budget.
  "js_error",
  "paid_cta_clicked",
  "report_checkout_abandoned",
  "report_checkout_recovery_clicked",
  "report_checkout_recovered",
  "report_checkout_started",
  "report_offer_cta_tapped",
  "report_delivered",
  "report_purchased",
  "revenue_completed",
  "razorpay_checkout_closed",
  "razorpay_checkout_opened",
  "shopify_cart_started",
  "shopify_outbound_click",
  "shopify_product_clicked",
]);

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pageHideHookInstalled = false;

export function beaconUrl(): string {
  // Same-origin in prod. Keep the trailing slash: page paths are slash-canonical,
  // and sendBeacon POST bodies must never cross a 301/308 redirect.
  // On localhost dev, fall back to the absolute Cloud Run path so dev
  // analytics still land somewhere without requiring a local Worker.
  if (typeof window === "undefined") return "/_beacon/";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
    // Dev: keep the legacy backend route (still wired).
    return "/_beacon/";
  }
  return "/_beacon/";
}

function ensurePageHideHook() {
  if (pageHideHookInstalled || typeof window === "undefined") return;
  pageHideHookInstalled = true;
  // `pagehide` is the modern replacement for `unload`; fires reliably on
  // mobile tab-close + back-nav where `beforeunload` doesn't.
  window.addEventListener(
    "pagehide",
    () => {
      flushQueue(true);
    },
    { capture: true }
  );
  // Best-effort fallback for tab visibility changes (e.g. user switches
  // apps mid-session — we want events from the previous batch to land).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushQueue(true);
    } else if (document.visibilityState === "visible") {
      pageVisibleSince = Date.now();
    }
  });
}

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue(false);
  }, BATCH_FLUSH_MS);
}

function flushQueue(useBeacon: boolean) {
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const url = beaconUrl();
  const payload = JSON.stringify({ events });
  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Browser analytics must never block the product.
  }
}

function enqueueOwnedEvent(
  name: string,
  properties: AnalyticsProperties | undefined,
  options: { forwardToGa4: boolean }
) {
  if (typeof window === "undefined") return;
  if (isAutomatedAuditBrowser()) return;
  const clientId = getGoogleAnalyticsClientId() ?? getOrCreateBootstrapDistinctId();
  if (!clientId) return;

  queue.push({
    name,
    client_id: clientId,
    // Stitch MP events to the gtag.js session so GA4 attributes the channel
    // correctly instead of opening a fresh (direct)/Unassigned session (rule 55).
    session_id: getGoogleAnalyticsSessionId(),
    forward_to_ga4: options.forwardToGa4,
    page_location: window.location.href,
    page_title: typeof document !== "undefined" ? document.title : "",
    page_referrer: typeof document !== "undefined" ? document.referrer : "",
    engagement_time_msec: currentEngagementMs(),
    params: ga4RawParams(name, {
      ...properties,
      client_event_ts: new Date().toISOString(),
      // Owned browser id — the SAME id recorded in Razorpay order notes, so
      // events_raw reconciles to server-canonical orders (stg_events_raw keys
      // on params.distinct_id, falling back to the GA client_id).
      distinct_id: getOrCreateBootstrapDistinctId(),
    }),
  });
  ensurePageHideHook();

  if (IMMEDIATE_FLUSH_EVENT_NAMES.has(name)) {
    flushQueue(false);
  } else if (queue.length >= BATCH_MAX_EVENTS) {
    flushQueue(false);
  } else {
    scheduleFlush();
  }
}

export function sendServerAnalyticsEvent(name: string, properties?: AnalyticsProperties) {
  if (!SERVER_EVENT_NAMES.has(name)) return;
  enqueueOwnedEvent(name, properties, {
    forwardToGa4: !GA4_EXCLUDED_SERVER_EVENT_NAMES.has(name),
  });
}

export function sendOwnedAnalyticsEvent(name: string, properties?: AnalyticsProperties) {
  enqueueOwnedEvent(name, properties, { forwardToGa4: false });
}
